
import { Helmet } from "react-helmet-async";

// =====================================================
// CONFIGURAZIONE GENERALE
// =====================================================

const SITE_URL = "https://micheleragowebdesigner.com";

const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const SOCIAL_PROFILES = [
  // Inserisci qui i tuoi profili reali:
  // "https://www.linkedin.com/in/tuo-profilo/",
  // "https://github.com/tuo-username/",
  // "https://www.instagram.com/tuo-profilo/"
];

// =====================================================
// CONTENUTI SEO PER LINGUA
// =====================================================

const SEO_CONTENT = {
  it: {
    title:
      "Web Designer Freelance & UX/UI Designer | Italia ed Europa",

    description:
      "Michele Rago, Web Designer e Front-End React Developer freelance. Realizzazione siti web professionali, UX/UI Design, landing page e sviluppo frontend per aziende, startup e professionisti in Italia e in Europa.",

    keywords:
      "web designer freelance, ux ui designer, ux designer, ui designer, react developer, front end developer, realizzazione siti web, sviluppo siti web, freelance web designer italia, web designer europa, ux ui design, landing page, seo",

    home: "Home",

    siteName: "Michele Rago | Web Designer & Developer",

    services: [
      "Web Design",
      "UX Design",
      "UI Design",
      "React Development",
      "Frontend Development",
      "Landing Page Design",
      "Website Development",
      "SEO"
    ]
  },

  en: {
    title:
      "Freelance Web Designer & UX/UI Designer | Europe",

    description:
      "Michele Rago is a freelance Web Designer and Front-End React Developer creating professional websites, landing pages and modern user interfaces for businesses, startups and professionals across Europe.",

    keywords:
      "freelance web designer, ux ui designer, ux designer, ui designer, react developer, frontend developer, web development, website design, european web designer, freelance web developer, landing page design, product design, seo",

    home: "Home",

    siteName: "Michele Rago | Web Designer & Developer",

    services: [
      "Web Design",
      "UX Design",
      "UI Design",
      "React Development",
      "Frontend Development",
      "Landing Page Design",
      "Website Development",
      "SEO"
    ]
  }
};

// =====================================================
// FUNZIONI DI SUPPORTO
// =====================================================

function normalizeUrl(url) {
  if (!url) return SITE_URL;

  return url.endsWith("/")
    ? url
    : `${url}/`;
}

function getAlternateUrls(url, lang, alternateUrls) {
  if (alternateUrls?.it && alternateUrls?.en) {
    return {
      it: normalizeUrl(alternateUrls.it),
      en: normalizeUrl(alternateUrls.en)
    };
  }

  const normalized = normalizeUrl(url);

  const pathname = normalized
    .replace(SITE_URL, "")
    .replace(/^\/en\/?/, "/");

  const cleanPath = pathname === "/" ? "" : pathname;

  return {
    it: `${SITE_URL}${cleanPath}/`,
    en: `${SITE_URL}/en${cleanPath}/`
  };
}

// =====================================================
// COMPONENTE SEO
// =====================================================

export default function SEO({
  lang = "it",

  title,
  description,
  keywords,

  url = SITE_URL,

  alternateUrls,

  image = DEFAULT_IMAGE,

  type = "website",

  author = "Michele Rago",

  pageName,

  breadcrumbs,

  areaServed = [
    "Italy",
    "Europe"
  ],

  sameAs = SOCIAL_PROFILES
}) {

  // Lingua selezionata
  const currentLang = lang === "en" ? "en" : "it";

  const content = SEO_CONTENT[currentLang];

  // URL canonical
  const canonicalUrl = normalizeUrl(url);

  // URL delle versioni linguistiche
  const languages = getAlternateUrls(
    canonicalUrl,
    currentLang,
    alternateUrls
  );

  // Contenuti SEO
  const seoTitle = title || content.title;

  const seoDescription =
    description || content.description;

  const seoKeywords =
    keywords || content.keywords;

  const currentPageName =
    pageName || content.home;

  // ===================================================
  // PERSON SCHEMA
  // ===================================================

  const personSchema = {

    "@type": "Person",

    "@id": `${SITE_URL}/#person`,

    name: "Michele Rago",

    url: SITE_URL,

    image,

    jobTitle:
      currentLang === "it"
        ? "Web Designer e Front-End Developer"
        : "Web Designer and Front-End Developer",

    description: seoDescription,

    knowsLanguage: [
      "Italian",
      "English"
    ],

    knowsAbout: [
      "Web Design",
      "UX Design",
      "UI Design",
      "React",
      "Frontend Development",
      "SEO",
      "Website Development",
      "Digital Product Design"
    ],

    sameAs
  };

  // ===================================================
  // PROFESSIONAL SERVICE SCHEMA
  // ===================================================

  const serviceSchema = {

    "@type": "ProfessionalService",

    "@id": `${SITE_URL}/#professional-service`,

    name: "Michele Rago Web Design",

    url: SITE_URL,

    image,

    description: seoDescription,

    founder: {
      "@id": `${SITE_URL}/#person`
    },

    areaServed: areaServed.map((area) => ({
      "@type": "Place",
      name: area
    })),

    availableLanguage: [
      "Italian",
      "English"
    ],

    serviceType: content.services
  };

  // ===================================================
  // WEBSITE SCHEMA
  // ===================================================

  const websiteSchema = {

    "@type": "WebSite",

    "@id": `${SITE_URL}/#website`,

    name: content.siteName,

    url: SITE_URL,

    description: content.description,

    inLanguage: [
      "it-IT",
      "en"
    ],

    publisher: {
      "@id": `${SITE_URL}/#person`
    }
  };

  // ===================================================
  // WEBPAGE SCHEMA
  // ===================================================

  const webpageSchema = {

    "@type": "WebPage",

    "@id": `${canonicalUrl}#webpage`,

    url: canonicalUrl,

    name: seoTitle,

    description: seoDescription,

    inLanguage:
      currentLang === "it" ? "it-IT" : "en",

    isPartOf: {
      "@id": `${SITE_URL}/#website`
    },

    about: {
      "@id": `${SITE_URL}/#person`
    },

    primaryImageOfPage: {
      "@type": "ImageObject",
      url: image
    }
  };

  // ===================================================
  // BREADCRUMB SCHEMA
  // ===================================================

  const breadcrumbItems = breadcrumbs || [
    {
      name: content.home,
      url: languages[currentLang]
    },
    {
      name: currentPageName,
      url: canonicalUrl
    }
  ];

  const breadcrumbSchema = {

    "@type": "BreadcrumbList",

    itemListElement: breadcrumbItems.map(
      (item, index) => ({

        "@type": "ListItem",

        position: index + 1,

        name: item.name,

        item: item.url
      })
    )
  };

  // ===================================================
  // SCHEMA GRAPH
  // ===================================================

  const structuredData = {

    "@context": "https://schema.org",

    "@graph": [
      personSchema,
      serviceSchema,
      websiteSchema,
      webpageSchema,
      breadcrumbSchema
    ]
  };

  // ===================================================
  // RENDER
  // ===================================================

  return (

    <Helmet>

      {/* LANGUAGE */}

      <html lang={currentLang === "it" ? "it" : "en"} />

      <title>{seoTitle}</title>

      <meta charSet="utf-8" />

      <meta
        name="viewport"
        content="width=device-width, initial-scale=1"
      />

      {/* BASIC SEO */}

      <meta
        name="description"
        content={seoDescription}
      />

      <meta
        name="keywords"
        content={seoKeywords}
      />

      <meta
        name="author"
        content={author}
      />

      <meta
        name="robots"
        content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
      />

      <meta
        name="googlebot"
        content="index,follow,max-image-preview:large"
      />

      <meta
        name="theme-color"
        content="#0A0A0A"
      />

      {/* CANONICAL */}

      <link
        rel="canonical"
        href={canonicalUrl}
      />

      {/* INTERNATIONAL SEO / HREFLANG */}

      <link
        rel="alternate"
        hrefLang="it-IT"
        href={languages.it}
      />

      <link
        rel="alternate"
        hrefLang="en"
        href={languages.en}
      />

      <link
        rel="alternate"
        hrefLang="x-default"
        href={languages.it}
      />

      {/* OPEN GRAPH */}

      <meta
        property="og:type"
        content={type}
      />

      <meta
        property="og:title"
        content={seoTitle}
      />

      <meta
        property="og:description"
        content={seoDescription}
      />

      <meta
        property="og:url"
        content={canonicalUrl}
      />

      <meta
        property="og:image"
        content={image}
      />

      <meta
        property="og:image:alt"
        content={seoTitle}
      />

      <meta
        property="og:site_name"
        content="Michele Rago"
      />

      <meta
        property="og:locale"
        content={
          currentLang === "it"
            ? "it_IT"
            : "en_GB"
        }
      />

      <meta
        property="og:locale:alternate"
        content={
          currentLang === "it"
            ? "en_GB"
            : "it_IT"
        }
      />

      {/* TWITTER / X */}

      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content={seoTitle}
      />

      <meta
        name="twitter:description"
        content={seoDescription}
      />

      <meta
        name="twitter:image"
        content={image}
      />

      {/* STRUCTURED DATA */}

      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

    </Helmet>

  );
}
